import type { Feed, Item, Workflow, CreateFeedRequest, UpdateFeedRequest, Activity, SchedulerStats, ScheduledItem, DeadJob, ActivityResponse, Podcast, PodcastEpisode, CreatePodcastRequest, UpdatePodcastRequest, PublishEpisodeRequest } from '@/types/models'

// ============================================================================
// Response Adapters (API → Frontend)
// ============================================================================

export function adaptListResponse<T>(
  apiResponse: any,
  key: string
): {
  data: T[]
  total: number
  limit: number
  offset: number
  hasMore: boolean
} {
  const data = apiResponse[key] || []

  return {
    data,
    // Use pagination info from API response if available, otherwise fallback to data length
    total: apiResponse.total ?? data.length,
    limit: apiResponse.limit ?? data.length,
    offset: apiResponse.offset ?? 0,
    hasMore: apiResponse.has_more ?? false,
  }
}

export function adaptFeedResponse(apiFeed: any): Feed {
  return {
    id: apiFeed.id,
    userId: apiFeed.user_id,
    name: apiFeed.name,
    url: apiFeed.config?.url || '',
    description: apiFeed.description,
    driver: apiFeed.driver_type,
    refreshInterval: parseInterval(apiFeed.refresh_interval),
    enabled: apiFeed.enabled,
    lastFetchAt: apiFeed.last_fetched_at,
    lastFetchStatus: mapFetchStatus(apiFeed.last_fetched_at, apiFeed.error_msg),
    errorMsg: apiFeed.error_msg || undefined,
    lastSuccess: apiFeed.last_success,
    itemCount: apiFeed.item_count || 0,
    unreadCount: apiFeed.unread_count || 0,
    config: {
      retentionDays: apiFeed.retention_days,
      maxItems: apiFeed.max_items,
      embeddingEnabled: apiFeed.embedding_enabled,
      autoSummarize: apiFeed.auto_summarize,
    },
    autoReadAge: apiFeed.auto_read_age || 0,
    metadata: apiFeed.metadata,
    createdAt: apiFeed.created_at,
    updatedAt: apiFeed.updated_at,
  }
}

export function adaptItemResponse(apiItem: any): Item {
  return {
    id: apiItem.id,
    feedId: apiItem.feed_id,
    title: apiItem.title,
    body: apiItem.body,
    summary: apiItem.summary || undefined,
    url: apiItem.url,
    author: apiItem.author,
    publishedAt: apiItem.published_at,
    uniqueId: apiItem.unique_id,
    isRead: apiItem.is_read,
    isFavorite: apiItem.is_favorite,
    metadata: apiItem.metadata,
    createdAt: apiItem.created_at,
    updatedAt: apiItem.updated_at,
  }
}

export function adaptWorkflowResponse(apiWorkflow: any): Workflow {
  return {
    id: apiWorkflow.id,
    userId: apiWorkflow.user_id,
    name: apiWorkflow.name,
    description: apiWorkflow.description,
    feedId: apiWorkflow.feed_id,
    enabled: apiWorkflow.enabled,
    trigger: {
      type: apiWorkflow.trigger_type || 'new_item',
      schedule: apiWorkflow.trigger_schedule,
      feedId: apiWorkflow.feed_id,
    },
    steps: (apiWorkflow.steps || []).map((step: any) => ({
      id: step.id,
      type: step.action_type,
      name: step.name || step.action_type,
      config: step.config,
      order: step.order,
    })),
    lastExecutedAt: apiWorkflow.last_run_at,
    executionCount: apiWorkflow.execution_count || 0,
    createdAt: apiWorkflow.created_at,
    updatedAt: apiWorkflow.updated_at,
  }
}

// The podcasts API wraps each podcast with its computed feed URL:
// { podcast: {...}, feed_url: "..." }
export function adaptPodcastResponse(apiEnvelope: any): Podcast {
  const p = apiEnvelope.podcast ?? apiEnvelope
  return {
    id: p.id,
    userId: p.user_id,
    token: p.token,
    title: p.title,
    description: p.description || undefined,
    author: p.author || undefined,
    artworkUrl: p.artwork_url || undefined,
    language: p.language,
    category: p.category,
    explicit: p.explicit ?? false,
    maxEpisodes: p.max_episodes ?? 0,
    feedUrl: apiEnvelope.feed_url || '',
    createdAt: p.created_at,
    updatedAt: p.updated_at,
  }
}

export function adaptEpisodeResponse(apiEpisode: any): PodcastEpisode {
  return {
    id: apiEpisode.id,
    podcastId: apiEpisode.podcast_id,
    itemId: apiEpisode.item_id ?? null,
    title: apiEpisode.title,
    description: apiEpisode.description || undefined,
    sourceUrl: apiEpisode.source_url || undefined,
    sizeBytes: apiEpisode.size_bytes ?? 0,
    durationSecs: apiEpisode.duration_secs ?? 0,
    status: apiEpisode.status,
    errorMsg: apiEpisode.error_msg || undefined,
    voice: apiEpisode.voice || undefined,
    publishedAt: apiEpisode.published_at,
    createdAt: apiEpisode.created_at,
    updatedAt: apiEpisode.updated_at,
  }
}

export function adaptPodcastRequest(data: CreatePodcastRequest | UpdatePodcastRequest): any {
  const request: any = {}
  if (data.title !== undefined) request.title = data.title
  if (data.description !== undefined) request.description = data.description
  if (data.author !== undefined) request.author = data.author
  if (data.artworkUrl !== undefined) request.artwork_url = data.artworkUrl
  if (data.language !== undefined) request.language = data.language
  if (data.category !== undefined) request.category = data.category
  if (data.explicit !== undefined) request.explicit = data.explicit
  if (data.maxEpisodes !== undefined) request.max_episodes = data.maxEpisodes
  return request
}

export function adaptPublishEpisodeRequest(data: PublishEpisodeRequest): any {
  const request: any = { item_id: data.itemId }
  if (data.source) request.source = data.source
  if (data.voice) request.voice = data.voice
  if (data.speed) request.speed = data.speed
  return request
}

export function adaptActivityResponse(apiActivity: any): Activity {
  return {
    id: apiActivity.id,
    type: apiActivity.type,
    status: apiActivity.status,
    startedAt: apiActivity.started_at,
    endedAt: apiActivity.ended_at,
    durationMs: apiActivity.duration_ms,
    errorMsg: apiActivity.error_msg,
    feedId: apiActivity.feed_id,
    feedName: apiActivity.feed_name,
    itemsNew: apiActivity.items_new,
    workflowId: apiActivity.workflow_id,
    workflowName: apiActivity.workflow_name,
    itemId: apiActivity.item_id,
    itemTitle: apiActivity.item_title,
  }
}

export function adaptActivityListResponse(apiResponse: any): ActivityResponse {
  const activities = (apiResponse.activities || []).map(adaptActivityResponse)
  return {
    activities,
    total: apiResponse.total ?? activities.length,
    limit: apiResponse.limit ?? activities.length,
    offset: apiResponse.offset ?? 0,
    hasMore: apiResponse.has_more ?? false,
  }
}

export function adaptScheduledItemResponse(apiItem: any): ScheduledItem {
  return {
    type: apiItem.type,
    targetId: apiItem.target_id,
    targetName: apiItem.target_name,
    driverType: apiItem.driver_type,
    userId: apiItem.user_id,
    nextRunAt: apiItem.next_run_at,
  }
}

export function adaptDeadJobResponse(apiJob: any): DeadJob {
  return {
    id: apiJob.id,
    type: apiJob.type,
    targetId: apiJob.target_id,
    userId: apiJob.user_id,
    attempt: apiJob.attempt,
    error: apiJob.error,
    createdAt: apiJob.created_at,
    failedAt: apiJob.failed_at,
  }
}

export function adaptSchedulerStatsResponse(apiStats: any): SchedulerStats {
  return {
    queueSize: apiStats.queue_size ?? 0,
    inFlightCount: apiStats.in_flight_count ?? 0,
    scheduledCount: apiStats.scheduled_count ?? 0,
    deadLetterCount: apiStats.dead_letter_count ?? 0,
    workerCount: apiStats.worker_count ?? 0,
    scheduledItems: apiStats.scheduled_items?.map(adaptScheduledItemResponse),
    deadJobs: apiStats.dead_jobs?.map(adaptDeadJobResponse),
  }
}

// ============================================================================
// Request Adapters (Frontend → API)
// ============================================================================

export function adaptItemFilters(filters?: any): any {
  if (!filters) return undefined

  const adapted: any = {}

  // Convert camelCase to snake_case
  if (filters.feedId !== undefined && filters.feedId !== null) adapted.feed_id = filters.feedId
  if (filters.isRead !== undefined) adapted.is_read = filters.isRead
  if (filters.isFavorite !== undefined) adapted.is_favorite = filters.isFavorite
  if (filters.since !== undefined) adapted.since = filters.since
  if (filters.until !== undefined) adapted.until = filters.until
  if (filters.limit !== undefined) adapted.limit = filters.limit
  if (filters.offset !== undefined) adapted.offset = filters.offset

  return adapted
}

export function adaptActivityFilters(filters?: any): any {
  if (!filters) return undefined

  const adapted: any = {}

  // Convert camelCase to snake_case
  if (filters.type !== undefined) adapted.type = filters.type
  if (filters.status !== undefined) adapted.status = filters.status
  if (filters.feedId !== undefined && filters.feedId !== null) adapted.feed_id = filters.feedId
  if (filters.workflowId !== undefined) adapted.workflow_id = filters.workflowId
  if (filters.since !== undefined) adapted.since = filters.since
  if (filters.until !== undefined) adapted.until = filters.until
  if (filters.limit !== undefined) adapted.limit = filters.limit
  if (filters.offset !== undefined) adapted.offset = filters.offset

  return adapted
}

export function adaptFeedRequest(frontendData: CreateFeedRequest | UpdateFeedRequest): any {
  const request: any = {}

  if ('name' in frontendData && frontendData.name) {
    request.name = frontendData.name
  }

  if ('driver' in frontendData && frontendData.driver) {
    request.driver_type = frontendData.driver
  }

  if ('url' in frontendData && frontendData.url) {
    request.config = { url: frontendData.url }
  }

  if ('refreshInterval' in frontendData && frontendData.refreshInterval !== undefined) {
    request.refresh_interval = formatInterval(frontendData.refreshInterval)
  }

  if ('description' in frontendData) {
    request.description = frontendData.description
  }

  if ('enabled' in frontendData) {
    request.enabled = frontendData.enabled
  }

  if ('autoReadAge' in frontendData && frontendData.autoReadAge !== undefined) {
    request.auto_read_age = frontendData.autoReadAge
  }

  if (frontendData.config) {
    request.retention_days = frontendData.config.retentionDays || 30
    request.max_items = frontendData.config.maxItems || 1000
    if (frontendData.config.embeddingEnabled !== undefined) {
      request.embedding_enabled = frontendData.config.embeddingEnabled
    }
    if (frontendData.config.autoSummarize !== undefined) {
      request.auto_summarize = frontendData.config.autoSummarize
    }
  }

  return request
}

export function adaptWorkflowRequest(frontendData: any): any {
  return {
    name: frontendData.name,
    description: frontendData.description,
    feed_id: frontendData.feedId,
    enabled: frontendData.enabled ?? true,
    trigger_type: frontendData.trigger?.type || 'new_item',
    trigger_schedule: frontendData.trigger?.schedule,
    error_policy: frontendData.errorPolicy || 'warn_continue',
    // order is 1-based: the API binds it as `required`, which rejects 0.
    steps: (frontendData.steps || []).map((step: any, index: number) => ({
      order: step.order && step.order > 0 ? step.order : index + 1,
      action_type: step.actionType ?? step.type,
      config: step.config,
    })),
  }
}

// ============================================================================
// Helpers
// ============================================================================

function parseInterval(interval?: string): number {
  // "30m" → 30, "1h" → 60, "auto" → 0
  if (!interval) return 30
  if (interval === 'auto') return 0
  const match = interval.match(/(\d+)([mh])/)
  if (!match || !match[1] || !match[2]) return 30
  const value = parseInt(match[1])
  const unit = match[2]
  return unit === 'h' ? value * 60 : value
}

function formatInterval(minutes: number | string): string {
  // "auto" or 0 → "auto", 30 → "30m", 60 → "1h"
  if (minutes === 'auto' || minutes === 0) return 'auto'
  const value = typeof minutes === 'string' ? parseInt(minutes) : minutes
  if (isNaN(value)) return 'auto'
  if (value >= 60 && value % 60 === 0) {
    return `${value / 60}h`
  }
  return `${value}m`
}

function mapFetchStatus(lastFetchedAt?: string, errorMsg?: string): 'success' | 'error' | 'pending' | 'never' {
  // If the feed has never been fetched, return 'never'
  if (!lastFetchedAt) return 'never'

  // Feed has been fetched - determine status based on error message
  if (!errorMsg || errorMsg === '') return 'success'
  return 'error'
}
